import { useState } from "react";

import { AddressWriteMutation, IAddressFormValues, IVerifyAddress } from "common/types/addresses";

import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import SelectPanel from "client/common/components/inputs/SelectPanel";
import { useCurrentDrawer } from "client/common/hooks/stackables";
import { useAddressUndeliverableDialog } from "client/accounts/stackables";

import styles from "../../styles/NewAddressConfirmationDrawer.module.less";

type AddressProps = {
  address: IAddressFormValues;
};

const Address = ({ address: { address_one, address_two, city, state, zipcode } }: AddressProps) => (
  <div className={styles.address}>
    <div>
      {address_one} {address_two}
    </div>
    <div>
      {city}, {state}, {zipcode}
    </div>
  </div>
);

export type NewAddressConfirmationDrawerProps = {
  data: IVerifyAddress;
  /** Called when the user commits to a deliverable address or proceeds-as-is on an undeliverable one. */
  onConfirm: (body: AddressWriteMutation) => void;
  /**
   * Called when the user opts to update an undeliverable address — the parent
   * form should highlight the implicated field and re-enable editing.
   */
  onUpdate?: (data: IVerifyAddress) => void;
};

/**
 * "Did you mean...?" picker. Always opens after a verify call returns
 * `needs_confirmation: true`, regardless of deliverability. If the user
 * clicks Save on an undeliverable address, this drawer hands off to
 * `AddressUndeliverableDialog` for the Update/Proceed choice.
 */
const NewAddressConfirmationDrawer = ({
  data,
  onConfirm,
  onUpdate,
}: NewAddressConfirmationDrawerProps) => {
  const { close } = useCurrentDrawer();
  const { open: openUndeliverableDialog } = useAddressUndeliverableDialog();
  const { address_fields: cleanedAddress, user_provided_address: userAddress } = data;
  const [useCleaned, setUseCleaned] = useState(!!cleanedAddress);
  const [isLoading, setIsLoading] = useState(false);

  const buildBody = (): AddressWriteMutation => ({
    ...data.user_provided_address,
    cache_key: data.cache_key,
    use_cached_address: useCleaned,
  });

  const onConfirmAddress = () => {
    if (!data.is_deliverable) {
      openUndeliverableDialog({
        data,
        onProceed: () => {
          close();
          onConfirm(buildBody());
        },
        onUpdate: () => {
          close();
          onUpdate?.(data);
        },
      });
      return;
    }
    setIsLoading(true);
    onConfirm(buildBody());
    close();
  };

  const cleanedOption = {
    label: "Suggested address",
    address: cleanedAddress,
    value: true,
  };

  const userOption = {
    label: "Inputted address",
    address: userAddress,
    value: false,
  };

  const options = [cleanedOption, userOption];

  return (
    <DrawerLayout>
      <DrawerLayout.Header
        title="Did you mean..."
        description="Review the address to make sure it's correct"
        onClose={close}
      />
      <DrawerLayout.Form
        submitText="Save address"
        isLoading={isLoading}
        onSubmit={onConfirmAddress}
        canSubmit={true}
      >
        <DrawerLayout.Form.Group>
          <DrawerLayout.Form.Label>Select an address</DrawerLayout.Form.Label>
          <SelectPanel
            entityName="Address"
            getKey={(address) => `${address?.value}`}
            options={options}
            renderOption={(option) => (
              <div className={styles.option}>
                <div className={styles.label}>{option.label}</div>
                <Address address={option.address} />
              </div>
            )}
            value={useCleaned ? cleanedOption : userOption}
            onClick={(option) => setUseCleaned(option.value)}
          />
        </DrawerLayout.Form.Group>
      </DrawerLayout.Form>
    </DrawerLayout>
  );
};

export default NewAddressConfirmationDrawer;
